/* ═══════════════════════════════════════════════════════════════════════════
   Subscription & Payment — service layer (REAL API)

   Adapter between the backend Billing/Credits API and the shapes the UI uses.
   Uses the shared `api` axios instance (baseURL = REACT_APP_TYPE_API_URL, Bearer
   token attached by the interceptor). See API_GUIDE.md.

   Not yet wired (Phase 2 — needs Stripe.js + REACT_APP_STRIPE_PUBLISHABLE_KEY):
   real card capture (setup-intent + confirmCardSetup) and the Stripe top-up
   confirmation. In dev the backend returns `record_only` top-ups (no card), which
   IS handled here.
   ═══════════════════════════════════════════════════════════════════════════ */

import api from "../../services/baseapi";
import {
  BillingCycle,
  CancellationReason,
  Invoice,
  PaymentMethod,
  Plan,
  PlanChangePreview,
  PlanChangeResult,
  PlanId,
  SeatUsage,
  Subscription,
  TokenBalance,
  TokenPack,
} from "./subscription.types";

// ── Helpers ──────────────────────────────────────────────────────────────────

const dollars = (cents?: number | null): number | null =>
  cents == null ? null : Math.round(cents) / 100;

const fmtDate = (iso?: string | null): string | null =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

const is404 = (e: any) => e?.response?.status === 404;

// Presentation bits the API doesn't return (taglines / "most popular").
const TAGLINE: Record<string, string> = {
  free: "Build and explore — export & translation stay locked",
  individual: "For independent consultants and solo researchers",
  professional: "For MEAL units, research teams, and growing organizations",
  enterprise: "For INGOs, agencies, and multi-country programs",
};

// Fixed display order for plan cards, independent of whatever order
// /billing/plans returns its array in (e.g. by db id/creation order).
const TIER_ORDER: Record<PlanId, number> = {
  free: 0,
  individual: 1,
  professional: 2,
  enterprise: 3,
};

/** Contact address surfaced in the "Upgrade to enterprise" modal. */
export const ENTERPRISE_CONTACT_EMAIL = "info@kaptechgroup.com";

// ── API response shapes (partial) ─────────────────────────────────────────────

interface ApiPrice {
  interval_code: string;
  interval_months: number;
  price_cents: number;
  /** Monthly price per seat beyond the plan's included_working_users, for this interval. */
  additional_user_price_cents?: number | null;
  currency: string;
  is_active: boolean;
}
interface ApiPlan {
  id: number;
  code: string;
  name: string;
  is_paid: boolean;
  working_user_cap: number | null;
  concurrent_project_cap: number | null;
  monthly_credits: number;
  price_cents: number;
  /** Seats bundled into price_cents at no extra cost (same regardless of interval). */
  included_working_users?: number | null;
  currency: string;
  prices: ApiPrice[];
  /** true = unlimited AI credits included (internal plans). */
  unlimited_credits?: boolean;
}
interface ApiSubscription {
  status: string;
  billing_interval: string;
  plan: ApiPlan;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  working_users: number;
  concurrent_projects: number;
  included_working_users?: number;
  billable_extra_users?: number;
  additional_user_price_cents?: number;
  /** Backend-computed total for the current period (base + extra seats) — authoritative. */
  current_total_cents?: number;
  /** Backend-computed projected next invoice, reflecting the live seat count. */
  next_invoice_total_cents?: number;
  /** Present on /billing/subscription/change when the card (e.g. 3DS) needs the customer to confirm. Off by default. */
  requires_action?: boolean;
  client_secret?: string | null;
}

const activePrice = (p: ApiPlan, interval: "month" | "year") =>
  p.prices?.find((pr) => pr.interval_code === interval && pr.is_active) ?? null;

/** Base plan price (dollars) for a plan + interval, or null. */
const basePrice = (p: ApiPlan | undefined, cycle: BillingCycle): number | null => {
  if (!p) return null;
  const pr = activePrice(p, cycle === "yearly" ? "year" : "month");
  return dollars(pr?.price_cents ?? null);
};

/** Price (dollars) per seat beyond included_working_users, for a plan + interval. */
const extraSeatPrice = (p: ApiPlan | undefined, cycle: BillingCycle): number => {
  if (!p) return 0;
  const pr = activePrice(p, cycle === "yearly" ? "year" : "month");
  return dollars(pr?.additional_user_price_cents ?? null) ?? 0;
};

/** Total tiered cost for `seats` working users on plan `p` + interval (base + extra seats). */
const planTotal = (p: ApiPlan | undefined, cycle: BillingCycle, seats: number): number => {
  if (!p) return 0;
  const base = basePrice(p, cycle) ?? 0;
  const included = p.included_working_users ?? 0;
  const extraSeats = Math.max(0, seats - included);
  return Math.round((base + extraSeats * extraSeatPrice(p, cycle)) * 100) / 100;
};

// ── Plans ──────────────────────────────────────────────────────────────────

const mapPlan = (p: ApiPlan): Plan => {
  const month = activePrice(p, "month");
  const year = activePrice(p, "year");
  const includedWorkingUsers = p.included_working_users ?? undefined;

  const baseMonthly = dollars(month?.price_cents ?? null);
  const baseYearlyMonthly = year ? Math.round(year.price_cents / 12) / 100 : null;

  let yearlySaveLabel: string | null = null;
  if (baseMonthly && baseYearlyMonthly && baseMonthly > 0) {
    const pct = Math.round((1 - baseYearlyMonthly / baseMonthly) * 100);
    if (pct > 0) yearlySaveLabel = `Save ${pct}%`;
  }

  // The card only renders the "Includes N users / add up to M more" price
  // breakdown for paid, non-custom-pricing plans (PlanCard's isFree/customPricing
  // branches short-circuit before ever reaching it) — so only suppress the
  // plain "Up to N working users" bullet where that breakdown actually shows,
  // or a plan like Free (which may also have includedWorkingUsers set, but no
  // breakdown block) would lose its only mention of the seat limit entirely.
  const showsSeatBreakdown =
    p.is_paid && p.code !== "enterprise" && includedWorkingUsers != null;

  const features = [
    p.concurrent_project_cap == null
      ? "Unlimited projects"
      : `${p.concurrent_project_cap} active project${p.concurrent_project_cap === 1 ? "" : "s"}`,
    ...(showsSeatBreakdown
      ? []
      : [
          p.working_user_cap == null
            ? "Unlimited working users"
            : `Up to ${p.working_user_cap} working user${p.working_user_cap === 1 ? "" : "s"}`,
        ]),
    "Contributor & Viewer free",
    ...(p.is_paid ? ["Export, publish & share"] : []),
    ...(p.unlimited_credits
      ? ["Unlimited AI credits"]
      : p.monthly_credits > 0
        ? [`${p.monthly_credits.toLocaleString()} AI credits / month`]
        : []),
  ];

  return {
    id: p.code as PlanId,
    name: p.name,
    tagline: TAGLINE[p.code] ?? "",
    monthlyPrice: baseMonthly,
    yearlyMonthlyPrice: baseYearlyMonthly,
    yearlyTotal: dollars(year?.price_cents ?? null),
    isFree: !p.is_paid,
    customPricing: p.code === "enterprise",
    mostPopular: p.code === "professional",
    yearlySaveLabel,
    workingUserLimit: p.working_user_cap,
    includedWorkingUsers,
    additionalUserPrice: extraSeatPrice(p, "monthly"),
    additionalUserPriceYearly: year
      ? Math.round((year.additional_user_price_cents ?? 0) / 12) / 100
      : undefined,
    concurrentProjectLimit: p.concurrent_project_cap,
    monthlyCredits: p.monthly_credits,
    unlimitedCredits: p.unlimited_credits,
    features,
  };
};

export const getPlans = async (): Promise<Plan[]> => {
  const { data } = await api.get<ApiPlan[]>("/billing/plans");
  return (data ?? [])
    .map(mapPlan)
    .sort((a, b) => (TIER_ORDER[a.id] ?? 99) - (TIER_ORDER[b.id] ?? 99));
};

// ── Payment methods ──────────────────────────────────────────────────────────

const getDefaultPaymentMethod = async (): Promise<PaymentMethod | null> => {
  try {
    const { data } = await api.get<any[]>("/billing/payment-methods");
    const list = data ?? [];
    const pm = list.find((m) => m.is_default) ?? list[0];
    if (!pm) return null;
    return {
      brand: pm.brand ?? "Card",
      last4: pm.last4 ?? "",
      expiry:
        pm.exp_month && pm.exp_year
          ? `${String(pm.exp_month).padStart(2, "0")}/${String(pm.exp_year).slice(-2)}`
          : "",
      cardholderName: "",
      country: "",
    };
  } catch {
    return null;
  }
};

// ── Subscription ─────────────────────────────────────────────────────────────

const rawSubscription = async (): Promise<ApiSubscription | null> => {
  try {
    const { data } = await api.get<ApiSubscription>("/billing/subscription");
    return data;
  } catch (e) {
    if (is404(e)) return null; // no subscription → free / not subscribed
    throw e;
  }
};

export const getSubscription = async (): Promise<Subscription> => {
  const [sub, paymentMethod] = await Promise.all([
    rawSubscription(),
    getDefaultPaymentMethod(),
  ]);

  if (!sub) {
    return {
      currentPlanId: "free",
      billingCycle: "monthly",
      status: "active",
      nextPaymentDue: null,
      nextInvoiceAmount: null,
      accessUntil: null,
      paymentMethod,
    };
  }

  const cancelled = sub.status === "canceled" || sub.cancel_at_period_end;
  return {
    currentPlanId: (sub.plan?.code ?? null) as PlanId | null,
    billingCycle: sub.billing_interval === "year" ? "yearly" : "monthly",
    status: cancelled ? "cancelled" : "active",
    nextPaymentDue: fmtDate(sub.current_period_end),
    nextInvoiceAmount: cancelled ? null : dollars(sub.next_invoice_total_cents ?? null),
    accessUntil: cancelled ? fmtDate(sub.current_period_end) : null,
    paymentMethod,
  };
};

export const getCurrentPlan = async (): Promise<Plan | null> => {
  const sub = await rawSubscription();
  if (!sub?.plan) return null;
  return mapPlan(sub.plan);
};

export const getSeatUsage = async (): Promise<SeatUsage> => {
  const sub = await rawSubscription();
  if (!sub) return { workingUsersUsed: 0, workingUserLimit: 1 };
  return {
    workingUsersUsed: sub.working_users ?? 0,
    workingUserLimit: sub.plan?.working_user_cap ?? null,
  };
};

// ── Invoices ─────────────────────────────────────────────────────────────────

export const getInvoices = async (
  page = 1,
  pageSize = 10,
): Promise<{ invoices: Invoice[]; total: number }> => {
  const { data } = await api.get<{ invoices: any[] }>("/billing/invoices");
  const all: Invoice[] = (data?.invoices ?? []).map((inv, i) => ({
    id: inv.id ?? String(i),
    fileName: inv.description ?? "Invoice",
    startDate: fmtDate(inv.created_at) ?? "",
    endDate: fmtDate(inv.created_at) ?? "",
    amount: dollars(inv.amount_cents) ?? 0,
    status: "paid" as const,
  }));
  const start = (page - 1) * pageSize;
  return { invoices: all.slice(start, start + pageSize), total: all.length };
};

const filenameFromContentDisposition = (
  headers: any,
  fallback: string,
): string => {
  const cd: string = headers?.["content-disposition"] || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  return match ? decodeURIComponent(match[1]) : fallback;
};

/** Download a single invoice/receipt file. */
export const downloadInvoice = async (
  invoiceId: string,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await api.get(`/billing/invoices/${invoiceId}/download`, {
    responseType: "blob",
  });
  return {
    blob: response.data,
    filename: filenameFromContentDisposition(
      response.headers,
      `invoice-${invoiceId}.pdf`,
    ),
  };
};

/** Download all invoices/receipts as a single ZIP file. */
export const downloadAllInvoices = async (): Promise<{
  blob: Blob;
  filename: string;
}> => {
  const response = await api.get("/billing/invoices/download-all", {
    responseType: "blob",
  });
  return {
    blob: response.data,
    filename: filenameFromContentDisposition(response.headers, "invoices.zip"),
  };
};

/** Trigger a browser download of an already-fetched file blob. */
export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ── Credits ──────────────────────────────────────────────────────────────────

const CHARS_PER_CREDIT = 1000; // 1 credit = 1,000 characters (backend metering)

export const getTokenBalance = async (): Promise<TokenBalance> => {
  const balance = await (async () => {
    try {
      const { data } = await api.get<{ balance: number }>("/credits/balance");
      return data?.balance ?? 0;
    } catch (e) {
      if (is404(e)) return 0;
      throw e;
    }
  })();

  // Non-critical: if this fails or the field name doesn't match, the balance
  // above still renders fine — the usage meter just falls back to hidden
  // (see TokensSection: it only shows the meter when usedThisPeriod > 0).
  // NOTE: field name is a best guess (`used_this_period`) — verify against
  // the real /credits/usage response and adjust if it differs.
  const usedThisPeriod = await (async () => {
    try {
      const { data } = await api.get<{ used_this_period?: number; used?: number }>(
        "/credits/usage",
      );
      return data?.used_this_period ?? data?.used ?? 0;
    } catch {
      return 0;
    }
  })();

  return {
    balance,
    usedThisPeriod,
    charsPerToken: CHARS_PER_CREDIT,
    pricePerToken: 0,
  };
};

export const getTokenPacks = async (): Promise<TokenPack[]> => {
  const { data } = await api.get<any[]>("/credits/packs");
  return (data ?? []).map((p) => ({
    id: p.code, // topup expects pack_code
    tokens: p.credits,
    price: dollars(p.price_cents) ?? 0,
    discountLabel: null,
  }));
};

export interface TopupResult {
  mode: "record_only" | "stripe";
  balance?: number; // record_only: new balance
  clientSecret?: string; // stripe: confirm with Stripe.js
  amountCents?: number;
}

/**
 * Start a credit top-up. `record_only` (dev) adds credits instantly and returns
 * the new balance. `stripe` returns a client_secret to confirm with Stripe.js;
 * the webhook then adds the credits (refresh the balance a moment later).
 */
export const startTopup = async (
  packId: string,
  idempotencyKey: string,
): Promise<TopupResult> => {
  // Backend dedupes on `reference` (or an `Idempotency-Key` header), not
  // `idempotency_key` in the body — confirmed with backend: a repeated
  // `reference` returns the original payment instead of charging again.
  const { data } = await api.post<any>("/credits/topup", {
    pack_code: packId,
    reference: idempotencyKey,
  });
  return {
    mode: data?.mode,
    balance: data?.balance,
    clientSecret: data?.client_secret,
    amountCents: data?.amount_cents,
  };
};

// ── Stripe card capture (Phase 2) ─────────────────────────────────────────────

/** Start saving a card: returns a SetupIntent client_secret to confirm. */
export const createSetupIntent = async (): Promise<{
  clientSecret: string;
  customerId: string;
}> => {
  const { data } = await api.post<any>("/billing/setup-intent");
  return { clientSecret: data.client_secret, customerId: data.customer_id };
};

/** Make a confirmed Stripe PaymentMethod the org's default card. */
export const setDefaultCard = async (
  stripePaymentMethodId: string,
): Promise<void> => {
  await api.put("/billing/payment-method", {
    stripe_payment_method_id: stripePaymentMethodId,
  });
};

/** The default saved card's Stripe id (for confirming a top-up), or null. */
export const getDefaultCardId = async (): Promise<string | null> => {
  try {
    const { data } = await api.get<any[]>("/billing/payment-methods");
    const list = data ?? [];
    const pm = list.find((m) => m.is_default) ?? list[0];
    return pm?.stripe_payment_method_id ?? null;
  } catch {
    return null;
  }
};

// ── Payment method (display-only fallback; real card capture = Phase 2) ────────

export const updatePaymentMethod = async (
  method: PaymentMethod,
): Promise<PaymentMethod> => {
  // Display-only path (no Stripe): stores brand/last4 for display. Requires an
  // existing subscription. TODO(stripe, Phase 2): setup-intent + confirmCardSetup
  // then PUT { stripe_payment_method_id }.
  await api.put("/billing/payment-method", {
    brand: method.brand,
    last4: method.last4,
  });
  return method;
};

// ── Plan changes ─────────────────────────────────────────────────────────────

const priceLabel = (p: ApiPlan | undefined, cycle: BillingCycle): string => {
  if (!p) return "";
  if (!p.is_paid) return "Free";
  if (p.code === "enterprise") return "Custom pricing";
  const amt = basePrice(p, cycle);
  return cycle === "yearly" ? `$${amt}/yr` : `$${amt}/mo`;
};

/**
 * Client-side proration preview (API_GUIDE Flow C). Stripe prorates on the
 * backend; nothing is charged today, the difference lands on the next invoice.
 * Uses the plan's tiered fields (included_working_users + additional_user_price_cents)
 * so this matches what /billing/subscription/change will actually bill —
 * NOT a flat price_cents × seats guess.
 */
export const previewPlanChange = async (
  targetId: PlanId,
  cycle: BillingCycle,
): Promise<PlanChangePreview> => {
  const [plansRaw, sub] = await Promise.all([
    api.get<ApiPlan[]>("/billing/plans").then((r) => r.data ?? []),
    rawSubscription(),
  ]);
  const target = plansRaw.find((p) => p.code === targetId);
  const current = sub ? plansRaw.find((p) => p.code === sub.plan?.code) : undefined;

  const seats = sub?.working_users || 1;
  const toLabel = target?.name ?? targetId;
  const includedSeats = target?.included_working_users ?? 0;
  const extraSeats = Math.max(0, seats - includedSeats);
  const newTotal = planTotal(target, cycle, seats);

  // First subscription (no current sub): full charge, effective now.
  if (!sub || !current) {
    return {
      direction: "upgrade",
      requiresPayment: newTotal > 0,
      fromLabel: "Free",
      fromPriceLabel: "Free",
      toLabel,
      toPriceLabel: priceLabel(target, cycle),
      effective: "Immediately",
      lineItems: [
        {
          label: `${toLabel} (${Math.min(seats, includedSeats)} of ${includedSeats} included seat${includedSeats === 1 ? "" : "s"})`,
          amount: basePrice(target, cycle) ?? 0,
        },
        ...(extraSeats > 0
          ? [
              {
                label: `${extraSeats} extra seat${extraSeats === 1 ? "" : "s"} × $${extraSeatPrice(target, cycle)}`,
                amount: Math.round(extraSeats * extraSeatPrice(target, cycle) * 100) / 100,
              },
            ]
          : []),
      ],
      creditAppliedToNextInvoice: null,
      creditNote: null,
      totalDueToday: newTotal,
      fromNextCycleLabel: `$${newTotal}/${cycle === "yearly" ? "yr" : "mo"}`,
      warning: null,
    };
  }

  const now = Date.now();
  const start = new Date(sub.current_period_start).getTime();
  const end = new Date(sub.current_period_end).getTime();
  const remainingFraction =
    end > start ? Math.min(1, Math.max(0, (end - now) / (end - start))) : 0;

  const currentCycle = sub.billing_interval === "year" ? "yearly" : "monthly";
  const currentTotal = planTotal(current, currentCycle, seats);

  const credit = Math.round(currentTotal * remainingFraction * 100) / 100;
  const charge = Math.round(newTotal * remainingFraction * 100) / 100;
  const netNextInvoice = Math.round((charge - credit) * 100) / 100;
  const direction =
    TIER_ORDER[targetId] >= TIER_ORDER[(sub.plan?.code as PlanId) ?? "free"]
      ? "upgrade"
      : "downgrade";

  return {
    direction,
    requiresPayment: false, // Stripe prorates onto the next invoice — nothing today
    fromLabel: current.name,
    fromPriceLabel: priceLabel(current, currentCycle),
    toLabel,
    toPriceLabel: priceLabel(target, cycle),
    effective: "Immediately",
    lineItems: [
      {
        label: `${current.name} credit (unused this period)`,
        amount: credit,
        credit: true,
      },
      { label: `${toLabel} (prorated)`, amount: charge },
    ],
    creditAppliedToNextInvoice: netNextInvoice > 0 ? netNextInvoice : null,
    creditNote: `Nothing is charged today. The prorated ${
      netNextInvoice >= 0 ? "difference" : "credit"
    } of $${Math.abs(netNextInvoice).toFixed(2)} lands on your next invoice.`,
    totalDueToday: 0,
    fromNextCycleLabel: `$${newTotal}/${cycle === "yearly" ? "yr" : "mo"} (${seats} seat${seats === 1 ? "" : "s"})`,
    warning:
      direction === "downgrade"
        ? `Users/projects above the ${toLabel} limit must be removed first, or the change is blocked.`
        : null,
  };
};

export const changePlan = async (
  targetId: PlanId,
  cycle: BillingCycle,
  preview: PlanChangePreview,
  idempotencyKey: string,
): Promise<PlanChangeResult> => {
  const { data } = await api.post<ApiSubscription>("/billing/subscription/change", {
    plan_code: targetId,
    interval: cycle === "yearly" ? "year" : "month",
    reason: "change",
    idempotency_key: idempotencyKey,
  });
  // Existing-subscription changes prorate onto the next invoice (nothing
  // charged today); a first-time paid signup charges the full amount today.
  // `preview` already computed which case this is — use it instead of
  // guessing, since the API response itself doesn't say what was charged.
  const totalDueToday = preview.requiresPayment ? preview.totalDueToday : 0;
  // Backend-computed and authoritative — reflects the tiered base+extra-seat
  // formula, so this is what will actually land on the next invoice.
  const nextTotal = dollars(data.next_invoice_total_cents ?? null);
  return {
    planLabel: data.plan?.name ?? targetId,
    planPriceLabel: priceLabel(data.plan, cycle),
    effective: "Today",
    cardCharged: totalDueToday > 0 ? `$${totalDueToday.toFixed(2)}` : "None",
    nextBilling: `${fmtDate(data.current_period_end) ?? ""}${
      nextTotal != null ? ` ($${nextTotal}/${cycle === "yearly" ? "yr" : "mo"})` : ""
    }`,
    totalDueToday,
    requiresAction: !!data.requires_action,
    clientSecret: data.client_secret ?? null,
  };
};

// ── Cancel / reactivate ──────────────────────────────────────────────────────

export const cancelSubscription = async (
  _reason: CancellationReason,
): Promise<{ accessUntil: string }> => {
  // Cancel at period end (keeps access until then). Reason is collected for UX
  // only; the API doesn't accept it.
  const { data } = await api.post<ApiSubscription>(
    "/billing/subscription/cancel?now_flag=false",
  );
  return { accessUntil: fmtDate(data?.current_period_end) ?? "" };
};

export const reactivateSubscription = async (
  _planId: PlanId,
  _cycle: BillingCycle,
): Promise<Subscription> => {
  // Undoes a pending cancellation (same plan). Switching to a *different* plan
  // from the cancelled state should go through changePlan instead.
  // No idempotency key: confirmed with backend this endpoint takes no body
  // and doesn't need dedup — it only reactivates within the still-active
  // paid period (no new invoice/charge), so there's nothing to double-submit.
  await api.post("/billing/subscription/reactivate");
  return getSubscription();
};
